import { isResourceKind } from '../api/types';
import type { OfferedResource, Resource, ResourceKind } from '../api/types';
import { useI18n } from '../i18n/useI18n';
import type { SimpleKey } from '../i18n/types';
import { DataTable } from './DataTable';
import type { Column } from './DataTable';
import { Labels } from './primitives';
import { KindIcon } from './KindIcon';
import { StatusBadge } from './StatusBadge';

/**
 * The declared catalogue as a table, in the one place both consoles read it
 * from.
 *
 * A tenancy's administrator writes this catalogue in their own cluster console;
 * a provider operator and the support desk look at the same rows in the
 * account. One component, so the two cannot drift into describing the same
 * resource differently — and `actions` is the only difference between them:
 * given, there is a column of controls; not given, there is no column at all.
 *
 * It is a setting of `DataTable` rather than a table of its own, which is where
 * the ordering comes from: a catalogue is the one list here that grows without
 * limit, and it was the one list that could not be put in any order.
 *
 * There is no serving connector here and no health. gdsgate has a field for
 * neither, and there is no signal that would say a site is on the air, so a
 * column filled with something plausible would be this console inventing the
 * one fact somebody would act on.
 */

/** The seven kinds gdsgate serves, in words. Exported: a form offers the same seven. */
export const KIND_LABEL: Record<ResourceKind, SimpleKey> = {
  ssh: 'resourceKind.ssh',
  postgres: 'resourceKind.postgres',
  mysql: 'resourceKind.mysql',
  kubernetes: 'resourceKind.kubernetes',
  tcp: 'resourceKind.tcp',
  mcp: 'resourceKind.mcp',
  llm: 'resourceKind.llm',
  web: 'resourceKind.web',
  'web-egress': 'resourceKind.webEgress',
};

/** A kind neither side knows reads as itself rather than as nothing. */
export function kindLabelKey(kind: string): SimpleKey | null {
  return isResourceKind(kind) ? KIND_LABEL[kind] : null;
}

/** Where a resource answers, spelled the way a client addresses it. */
function addressOf(resource: Resource): string {
  const host = resource.hostname ?? resource.id ?? '';
  return resource.port === undefined ? host : `${host}:${String(resource.port)}`;
}

/**
 * How long a lease has left, in the coarsest unit that still says something.
 *
 * A row whose lease has run out is still listed until the cluster's sweep takes
 * it, so "expired" is a state a person sees and not an impossible one.
 */
function leaseLeft(expiresAt: number | undefined, t: Translate): string {
  if (expiresAt === undefined) {
    return '';
  }
  const left = expiresAt - Math.floor(Date.now() / 1000);
  if (left <= 0) {
    return t('catalog.lease.expired');
  }
  if (left < 60) {
    return t('catalog.lease.secs', { secs: left });
  }
  if (left < 3600) {
    return t('catalog.lease.mins', { mins: Math.floor(left / 60) });
  }
  return t('catalog.lease.hours', { hours: Math.floor(left / 3600) });
}

/** The translator this file passes around, as `useI18n` hands it over. */
type Translate = ReturnType<typeof useI18n>['t'];

export function ResourceTable({
  resources,
  total,
  empty,
  noMatch,
  offered,
  serving,
  onOpen,
}: {
  resources: Resource[];
  /** How many rows before any filter. Absent means these are all there are. */
  total?: number;
  empty?: { title: string; body?: string };
  noMatch?: { title: string; body?: string };
  /**
   * What connectors say they serve, so a row can show who answers for it.
   *
   * Absent where the caller has not asked the cluster — the account's read-only
   * views — and the two columns then say nothing rather than saying "nobody",
   * which would be a claim nobody checked.
   */
  offered?: readonly OfferedResource[];
  /**
   * Resource ids the gateway is routing to right now.
   *
   * Absent where the caller has not asked — the account's read-only views — and
   * the column then says nothing rather than saying "offline", which would be a
   * claim nobody checked.
   */
  serving?: readonly string[];
  /** Given, a row opens the panel. */
  onOpen?: (resource: Resource) => void;
}) {
  const { t } = useI18n();

  const offerFor = (resource: Resource): OfferedResource | undefined =>
    offered?.find((offer) => offer.resource_id === (resource.id ?? resource.hostname));

  const columns: Column<Resource>[] = [
    {
      key: 'id',
      header: t('catalog.col.id'),
      sort: (a, b) => (a.id ?? '').localeCompare(b.id ?? ''),
      cell: (resource) => (
        <span className="cell-flex across">
          <KindIcon kind={resource.kind} />
          <span className="t-name mono">{resource.id}</span>
          {resource.parent !== undefined && (
            <span className="t-sub mono">{t('catalog.under', { parent: resource.parent })}</span>
          )}
        </span>
      ),
    },
    {
      key: 'kind',
      header: t('catalog.col.kind'),
      sort: (a, b) => a.kind.localeCompare(b.kind),
      cell: (resource) => {
        const label = kindLabelKey(resource.kind);
        return <span className="chip">{label === null ? resource.kind : t(label)}</span>;
      },
    },
    {
      key: 'address',
      muted: true,
      header: t('catalog.col.address'),
      mono: true,
      hideSm: true,
      sort: (a, b) => addressOf(a).localeCompare(addressOf(b)),
      cell: (resource) => addressOf(resource),
    },
    {
      // A row written inside a boundary, and how long it has left. A catalogue
      // that shows these the same as declared rows shows a list about to
      // shrink with no reason given for it.
      key: 'lease',
      header: t('catalog.col.lease'),
      muted: true,
      hideSm: true,
      sort: (a, b) => (a.lease_expires_at ?? 0) - (b.lease_expires_at ?? 0),
      cell: (resource) =>
        resource.scope === undefined ? (
          '—'
        ) : (
          <span className="cell-flex across">
            <span className="chip mono">{resource.scope}</span>
            <span className="t-sub">{leaseLeft(resource.lease_expires_at, t)}</span>
          </span>
        ),
    },
    {
      key: 'labels',
      header: t('catalog.col.labels'),
      hideSm: true,
      cell: (resource) => <Labels labels={resource.labels} empty={t('catalog.noLabels')} />,
    },
    {
      key: 'connector',
      header: t('catalog.col.connector'),
      muted: true,
      mono: true,
      hideSm: true,
      cell: (resource) => {
        const serving = offerFor(resource);
        return serving === undefined ? '—' : (serving.claimed_name ?? serving.node_id);
      },
    },
    {
      // The gateway's routing table, read at the moment of the listing: this
      // says whether anything is behind the row *now*.
      //
      // Not the offered listing, which is what a connector stated when its
      // tunnel opened, from memory — silent for a backend added since and for
      // a connector that has not restarted. Reading that as liveness is how
      // this column once painted every live resource `Offline`.
      //
      // `serving` absent altogether means nobody asked the gateway — a view
      // that does not have it says nothing rather than saying no.
      key: 'served',
      header: t('catalog.col.served'),
      cell: (resource) => {
        if (serving === undefined) {
          return (
            <span className="muted" title={t('catalog.served.unknown.hint')}>
              {t('catalog.served.unknown')}
            </span>
          );
        }
        return serving.includes(resource.id ?? '') ? (
          <StatusBadge tone="ok" label={t('catalog.served.yes')} />
        ) : (
          <StatusBadge
            tone="deny"
            label={t('catalog.served.no')}
            hint={t('catalog.served.no.hint')}
          />
        );
      },
    },
  ];


  return (
    <DataTable<Resource>
      caption={t('catalog.title')}
      columns={columns}
      rows={resources}
      {...(total === undefined ? {} : { total })}
      rowKey={(resource) => resource.id ?? resource.hostname ?? ''}
      {...(onOpen === undefined ? {} : { onRowClick: onOpen })}
      initialSort={{ key: 'id', direction: 'asc' }}
      empty={empty ?? { title: t('catalog.none.title'), body: t('catalog.none.body') }}
      noMatch={noMatch ?? { title: t('catalog.empty.title'), body: t('catalog.empty.body') }}
    />
  );
}
